import GenericTable from "@/components/GenericTable";
import Footer from "@/components/Footer";
import { ROUTES } from "@/config/routes";
import { LEDGER_TABLE_COLUMNS } from "@/config/tables";
import { useTableFiltersCursor } from "@/hooks/useTableFiltersCursor";
import { Badge, Box, HStack, Table, Text } from "@chakra-ui/react";
import { ArrowRight, MoveDownLeft, MoveUpRight } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFetchLedger } from "../hooks/useFetchLedger";
import { useFetchPayout } from "../hooks/useFetchPayout";
import { useFetchWithdrawal } from "../hooks/useFetchWithdrawal";
import { mapLedgerType } from "../mappers/payouts.mapper";
import type { LedgerMovement, LedgerFiltersType } from "../types/payouts.type";
import WithdrawalDetailModal from "./WithdrawalDetailModal";
import LedgerFilters from "./LedgerFilters";

const TABLE_KEY = "payouts-ledger";

export default function LedgerTable() {
  const { queryFilters, setQueryFilters, cursor, setCursor } =
    useTableFiltersCursor<LedgerFiltersType>(TABLE_KEY);

  const [selectedWithdrawalId, setSelectedWithdrawalId] = useState<
    string | null
  >(null);
  const [pendingPayoutId, setPendingPayoutId] = useState<string | null>(null);

  const { withdrawal, isLoadingWithdrawal } =
    useFetchWithdrawal(selectedWithdrawalId);
  const { payout } = useFetchPayout(pendingPayoutId);

  const { ledger, nextCursor, isLoading } = useFetchLedger(
    queryFilters,
    cursor
  );
  const navigate = useNavigate();

  const columns = [
    { key: "sourceType", label: "Tipo" },
    { key: "createdAt", label: "Fecha" },
    { key: "sourceId", label: "Referencia" },
    { key: "amount", label: "Monto" },
    { key: "resultingBalance", label: "Saldo" },
    { key: "action", label: "" },
  ];

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Intl.DateTimeFormat("es-CO", {
      dateStyle: "medium",
      timeStyle: "short",
      timeZone: "America/Bogota",
    }).format(new Date(dateString));
  };

  type LedgerType = keyof typeof mapLedgerType;

  const getTypeInfo = (type: LedgerMovement["sourceType"]) => {
    const mapping = mapLedgerType[type as LedgerType];
    if (!mapping) {
      return { label: type, color: "gray" as const };
    }
    return { label: mapping.label, color: mapping.color };
  };

  const handleRowClick = (entry: LedgerMovement) => {
    if (entry.sourceType === "settlement") {
      navigate(
        `${ROUTES.DASHBOARD.SETTLEMENT_DETAIL.replace(
          ":settlementId",
          entry.sourceId
        )}?detail=invoices`
      );
    } else if (entry.sourceType === "payoutBatch") {
      navigate(
        ROUTES.DASHBOARD.PAYOUTS_BATCH.replace(":batchId", entry.sourceId)
      );
    } else if (entry.sourceType === "payoutRefund") {
      setPendingPayoutId(entry.sourceId);
    } else if (
      entry.sourceType === "withdrawal" ||
      entry.sourceType === "withdrawalRefund"
    ) {
      setSelectedWithdrawalId(entry.sourceId);
    }
  };

  useEffect(() => {
    if (pendingPayoutId && payout) {
      navigate(
        `${ROUTES.DASHBOARD.PAYOUTS_BATCH.replace(
          ":batchId",
          payout.batchId
        )}?payoutId=${pendingPayoutId}`
      );
      setPendingPayoutId(null);
    }
  }, [pendingPayoutId, payout, navigate]);

  return (
    <Box display="flex" flexDirection="column" flex="1" minH={0}>
      <LedgerFilters
        queryFilters={queryFilters}
        setQueryFilters={setQueryFilters}
      />
      <GenericTable
        isLoading={isLoading}
        columns={columns}
        interactive
        fillHeight
      >
        {ledger.map((entry) => (
          <Table.Row
            key={entry.sourceId}
            onClick={() => handleRowClick(entry)}
            cursor="pointer"
          >
            <Table.Cell>
              <HStack>
                {(() => {
                  const info = getTypeInfo(entry.sourceType);
                  // Inflows (settlements, refunds) come back into the
                  // balance; outflows (payouts, withdrawals) leave it.
                  const isInflow = entry.amount > 0;
                  const isSettlement = entry.sourceType === "settlement";
                  return (
                    <>
                      <Box
                        p={2}
                        bg={`${isSettlement ? "green" : info.color}.50`}
                        color={`${isSettlement ? "green" : info.color}.600`}
                        borderRadius="md"
                      >
                        {isInflow ? (
                          <MoveDownLeft size={16} />
                        ) : (
                          <MoveUpRight size={16} />
                        )}
                      </Box>
                      <Text fontWeight="medium">{info.label}</Text>
                    </>
                  );
                })()}
              </HStack>
            </Table.Cell>
            <Table.Cell color="gray.600">
              {formatDate(entry.createdAt)}
            </Table.Cell>
            <Table.Cell>
              <Badge colorPalette="gray" variant="subtle">
                {entry.sourceId}
              </Badge>
            </Table.Cell>
            <Table.Cell>
              <Text
                fontWeight="bold"
                color={entry.amount > 0 ? "green.600" : "gray.800"}
              >
                {entry.amount > 0 ? "+" : ""}
                {formatCurrency(entry.amount)}
              </Text>
            </Table.Cell>
            <Table.Cell>
              <Text fontWeight="medium" color="gray.800">
                {formatCurrency(entry.resultingBalance)}
              </Text>
            </Table.Cell>
            <Table.Cell>
              <ArrowRight size={16} color="#CBD5E1" />
            </Table.Cell>
          </Table.Row>
        ))}
      </GenericTable>
      <Box mt="auto" pt={6} flexShrink={0}>
        <Footer
          tableProps={{
            fileName: "movimientos",
            items: ledger,
            columns: LEDGER_TABLE_COLUMNS,
            pagination: {
              tableKey: TABLE_KEY,
              nextCursor,
              setCursor,
              cursor,
            },
          }}
        />
      </Box>
      <WithdrawalDetailModal
        isOpen={!!selectedWithdrawalId}
        onClose={() => setSelectedWithdrawalId(null)}
        withdrawal={withdrawal ?? null}
        isLoading={isLoadingWithdrawal}
      />
    </Box>
  );
}
